import { Box, Typography, Button, IconButton, Tooltip, Modal, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AppConfig } from 'AppConfig';
import SmartCard from 'common/bstCard/smartCard';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { hasPermission } from 'utils/common';
import { store } from 'store/store';
import { useMediaQuery } from 'react-responsive';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import * as Actions from './actions/actions';
import { fetchFeatureReports } from './defaultSetting/actions/action';
import NoAvailableReports from 'features/frontoffice/component/noAvailableReports';
import axios from 'axios';
import { FoMaterialTable } from 'common/FoMaterialTable';
import HideSmartCardDialog from 'features/landing/newHome/HideSmartCardDialog';
import { IconArrowNarrowLeft, IconCircleCheck, IconX } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { showRestoreToast } from 'utils/showRestoreToast';
import SmartCardsLoading from './component/smartCardsLoading';
import Link from '@mui/material/Link';
import { buildMetricsFilters, getFilterHash } from 'features/metrics/metricsActions';
import { useSmartCardVdsTrigger } from 'features/frontoffice/useSmartCardVdsTrigger';

const isAvailable = (arr) => {
  return arr && Array.isArray(arr) && arr.length > 0;
};

const HiddenSmartCard = ({sortBy,searchTerm = ''}) => {
  useSmartCardVdsTrigger();
  const isMobil = useMediaQuery({ query: '(min-width: 320px) and (max-width: 767px)' });
  const isTablet = useMediaQuery({ query: '(min-width: 768px) and (max-width: 1279px)' });
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);
  const navigate = useNavigate();
  const currentApp = useSelector((state) => state.user.currentApp);
  const smarts = useSelector((state) => state.fo.smarts);
  const metrics = useSelector((state) => state.fo.metrics);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const permissions = useSelector((state) => state.user.permissions);
  const showDismiss = hasPermission(AppConfig.PERMISSION.FO_SMARTCARD_DISMISS, permissions);
  const showRecover = hasPermission(AppConfig.PERMISSION.FO_SMARTCARD_RECOVER, permissions);
  const [showSmartCard, setShowSmartCard] = useState(false);
  const [selectedSmartCardId, setSelectedSmartCardId] = useState({ id: null, title: '' });
  const displaySmarts = smarts?.filter((s) => s.category === AppConfig.REPORT_CATEGORY.SMART.code && s.dismissed === true) || [];
  const sortKey = typeof sortBy === 'string' ? sortBy : sortBy?.value;
  const vdsLoadingFlag = useSelector((state) => state.fo.smartCardVdsLoading);
  const vdsSettledFilterHash = useSelector((state) => state.fo.smartCardVdsFilterHash);
  const appliedFilters = useSelector((state) => state.globalFilters?.applied);
  const defaultsSeeded = useSelector((state) => state.globalFilters?.defaultsSeeded);
  const appliedFilterHash = useMemo(
    () => getFilterHash(buildMetricsFilters(appliedFilters)),
    [appliedFilters]
  );
  const isVdsLoading = !defaultsSeeded || vdsLoadingFlag || appliedFilterHash !== vdsSettledFilterHash;
  const isLoading = smarts === null || smarts === undefined;
  const hiddenCount = useSelector((state)=>(state.fo.smarts || []).filter((s) => s.category === AppConfig.REPORT_CATEGORY.SMART.code && s.dismissed === true).length);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isUnhiding, setIsUnhiding] = useState(false);
  useEffect(()=>{
    if(isLoading) return;
    if(isUnhiding) return;
    if(hiddenCount === 0){
      navigate("/bi/reports", {
        state : {
          restored : true
        }
      });
    }
  }, [hiddenCount, isLoading, isUnhiding, navigate]);

  const sortedSmarts = useMemo(() => {
    const items = Array.isArray(displaySmarts) ? displaySmarts.slice() : [];

    const getFirstNumber = (item, keys) => {
      for (const key of keys) {
        const value = item?.[key];
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string') {
          const parsed = Number(value);
          if (Number.isFinite(parsed)) return parsed;
        }
      }
      return 0;
    };

    const toDateMs = (value) => {
      if (value == null) return 0;
      if (typeof value === 'number' && Number.isFinite(value)) {
        if (value > 1e12) return value;
        if (value > 1e9) return value * 1000;
        return 0;
      }
      const ms = Date.parse(String(value));
      return Number.isNaN(ms) ? 0 : ms;
    };

    const alpha = (a, b) => (a?.displayText ?? '').localeCompare(b?.displayText ?? '', undefined, { sensitivity: 'base' });

    const getViewedCount = (item) => getFirstNumber(item, ['viewedCount']);
    const getNeedsAttentionRank = (item) => getFirstNumber(item, ['rankTrend']);
    const getBenPickOrder = (item) => getFirstNumber(item, ['globalOrder']);

    switch (sortKey) {
      // Smart Card sort options (see SMART_CARD_SORT_OPTIONS)
      case 'needs_attention':
        return items.sort((a, b) => {
          const ar = getNeedsAttentionRank(a);
          const br = getNeedsAttentionRank(b);
          if (ar && br && ar !== br) return ar - br; // smaller rank = higher trending
          if ((ar || br) && ar !== br) return ar ? -1 : 1;

          const bv = getViewedCount(b);
          const av = getViewedCount(a);
          if (bv !== av) return bv - av;
          return alpha(a, b);
        });

      case 'ben_insights_pick':
        return items.sort((a, b) => {
          const ao = getBenPickOrder(a);
          const bo = getBenPickOrder(b);
          if (ao && bo && ao !== bo) return ao - bo;
          if ((ao || bo) && ao !== bo) return ao ? -1 : 1;
          return alpha(a, b);
        });

      case 'newest':
        return items.sort((a, b) => {
          const bd = Math.max(
            toDateMs(b?.updateDt)
          );
          const ad = Math.max(
            toDateMs(a?.updateDt)
          );
          if (bd !== ad) return bd - ad;
          return alpha(a, b);
        });

      case 'most_viewed':
      default:
         return items.sort((a, b) => {
          const bv = getViewedCount(b);
          const av = getViewedCount(a);
          if (bv !== av) return bv - av;

          return alpha(a, b);
        });
    }
  }, [smarts, sortKey]);

  useEffect(()=>{
    return () =>{
      toast.dismiss("restore-success-id");
    };
  } ,[]);

    const handleUnHiddenSmartCard = () => {
          if (!displaySmarts) return;
          const reportIds = displaySmarts.map((item)=>item.id);
          if(reportIds.length === 0) return;
          setIsRedirecting(true);
          setIsUnhiding(true);
          store.dispatch(Actions.unhiddenReport(reportIds)).then(()=>{
            showRestoreToast("All Hidden Smart Cards Restored" , "331px" , "233px" , "#005C2C" , false);
          }).catch(() => {
            showRestoreToast("Could not restore Smart Card(s)." , "320px" , "220px" , "#88041E" , true);
          });
          navigate('/bi/reports');
    };

  const searchedSmarts = useMemo(() => {
    const list = Array.isArray(sortedSmarts) ? sortedSmarts : [];
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return list;
    return list.filter((item) => {
      const title = (item?.displayText || '').toLowerCase();
      const desc = (item?.description || '').toLowerCase();
      return title.includes(term) || desc.includes(term);
    });
  }, [sortedSmarts, searchTerm]);

  const showLoadingState =
    isLoading || isVdsLoading || ((isUnhiding || isRedirecting) && searchedSmarts.length === 0);
  const showNoHiddenSmartCards = !showLoadingState && searchedSmarts.length === 0;

  const unhideTransitionHandlers = {
    onUnhideStart: () => {
      setIsUnhiding(true);
      setIsRedirecting(hiddenCount === 1);
    },
    onUnhideSettled: () => setIsUnhiding(false),
    onUnhideError: () => {
      setIsUnhiding(false);
      setIsRedirecting(false);
    }
  };

  const [, setSelectedMetrics] = useState(new Set());
  const [, setMetrixPanelClosed] = useState(true);

  /* Recovery dialog state */
  const [chartRecoveryModal, setChartRecoveryModal] = useState(false);
  const [recoverableCharts, setRecoverableCharts] = useState([]);
  const [selectedRecoverableCharts, setSelectedRecoverableCharts] = useState([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const refresh = useCallback(() => {
    store.dispatch(Actions.getFoDetail(currentTenantCd));
    store.dispatch(Actions.fetchMetricsByTenant());
    store.dispatch(fetchFeatureReports());
  }, [currentTenantCd]);

  /**
   * Recovers selected smart cards
   */
  const recoverCharts = () => {
    axios
      .post(`${AppConfig.FO_INITIAL_URL}/init/recovercharts`, selectedRecoverableCharts)
      .then(() => {
        setChartRecoveryModal(false);
        setRecoverableCharts([]);
        setSelectedRecoverableCharts([]);
        // Refresh data without page reload
        refresh();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  /**
   * Cancels recovery dialog
   */
  const onRecoveryCancel = () => {
    setChartRecoveryModal(false);
    setRecoverableCharts([]);
    setSelectedRecoverableCharts([]);
  };

  useEffect(() => {
    if (isAvailable(metrics)) {
      const newSelected = new Set();
      metrics.forEach((m) => {
        newSelected.add(m.displayText);
      });
      setMetrixPanelClosed(true); // Work around for bug BSTP-1267
      setSelectedMetrics(newSelected);
    } else {
      setSelectedMetrics(new Set());
      setMetrixPanelClosed(true);
    }
  }, [metrics]);

  const handleHideSmartCard = () => {
    if (!selectedSmartCardId) return;
    store.dispatch(Actions.dismissReport(selectedSmartCardId?.id));
    setShowSmartCard(false);
    setSelectedSmartCardId({ id: null, title: '' });
  };

  useEffect(() => {
    if (currentApp === AppConfig.APP_CODE_FO) {
      refresh();
    }
  }, [currentApp, refresh]);

  // Refresh when navigating to Smart Cards page to sync favorites from Home
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = prevPathnameRef.current;

    // Only trigger if we navigated FROM Home page TO Smart Cards page
    if ((previousPath === '/frontoffice' || previousPath === '/frontoffice/') && currentPath.includes('/frontoffice/smartcards')) {
      refresh();
    }

    // Update previous pathname
    prevPathnameRef.current = currentPath;
  }, [location.pathname, refresh]);

  return (
    <Box sx={{ bgcolor: '#FFFFFF', width: '100%' }}>
      {isMobil ? (
        <Box sx={{ px: '24px', mx: '-60px', pt: 4, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
          <Link
            component={RouterLink}
            to="/bi/reports"
            underline="none"
            sx={{
              fontSize: '14px',
              fontStyle: 'normal',
              fontFamily: 'Noto Sans',
              fontWeight: 600,
              color: '#2F54A5',
              display: 'inline-flex',
              alignItems: 'center',
              lineHeight: '150%',
              letterSpacing: '0%',
              gap: 0.5,
              '&:hover': { color: '#068DF1', textDecoration: 'none' },
              whiteSpace: 'nowrap',
              alignSelf: 'flex-start',
              mb: 3,
            }}>
            <IconArrowNarrowLeft size={16} />
            Back
          </Link>

          <Typography
            sx={{
              color: '#000',
              fontFamily: 'Rubik',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '150%',
              letterSpacing: '0.16px',
              mt: '12px',
            }}>
            {`Hidden (${displaySmarts.length})`}
          </Typography>

          <Button
            disableElevation
            onClick={handleUnHiddenSmartCard}
            sx={{
              borderRadius: '24px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minWidth: 0,
              textTransform: 'none',
              padding: '0 16px',
              fontWeight: 600,
              textAlign: 'center',
              color: '#ffffff',
              fontSize: '14px',
              lineHeight: '21px',
              fontFamily: 'Noto Sans',
              fontStyle: 'normal',
              backgroundColor: '#2F54A5',
              '&:hover': { backgroundColor: '#344e91' },
              mt: '12px',
            }}>
            Unhide All Smart Cards
          </Button>

          <Box sx={{ display: 'flex', width: '100%', flexWrap: 'wrap', gap: '16px', mt: '16px', justifyContent: 'center' }}>
            {showLoadingState ? (
              <SmartCardsLoading count={3} variant="stack" />
            ) : showNoHiddenSmartCards ? (
                <NoAvailableReports isSmartCards />
            ) : (
              searchedSmarts.map((smart, idx) => (
                <Box
                  key={smart.scId || idx}
                  sx={{ width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', px: '8px', mb: '16px' }}>
                  <SmartCard
                    item={smart}
                    reportType={AppConfig.SMART_CAROUSEL}
                    showDismiss={showDismiss}
                    permissions={permissions}
                    setShowSmartCard={setShowSmartCard}
                    setSelectedSmartCardId={setSelectedSmartCardId}
                    hiddenCount={hiddenCount}
                    {...unhideTransitionHandlers}
                  />
                </Box>
              ))
            )}
          </Box>
        </Box>
      ) : isTablet ? (
        <Box sx={{ mx: 'auto', pt: 4 }}>
           <>
            <Link
              component={RouterLink}
              to="/bi/reports"
              underline="none"
              sx={{
                fontSize: '14px',
                fontStyle: 'normal',
                fontFamily: 'Noto Sans',
                fontWeight: 600,
                color: '#2F54A5',
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: '150%',
                letterSpacing: '0%',
                gap: 0.5,
                '&:hover': { color: '#068DF1', textDecoration: 'none' },
                whiteSpace: 'nowrap',
                mb: 3,
              }}>
              <IconArrowNarrowLeft size={16} />
              Back
            </Link>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                <Typography
                  sx={{
                    alignSelf: 'stretch',
                    color: '#000',
                    fontFamily: 'Rubik',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '150%',
                    width: '922px',
                    height: '24px',
                    letterSpacing: '0.16px'
                  }}>
                  {`Hidden (${displaySmarts.length})`}
                </Typography>
                <Button
                  disableElevation
                  sx={{
                    borderRadius: '24px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: {xs: '100%', sm: '100%', md: '182px'},
                    alignSelf: {xs: 'stretch', sm: 'stretch', md: 'flex-end'},
                    minwidth: 0,
                    textTransform: 'none',
                    marginBottom: '12px',
                    fontWeight: 600,
                    textAlign: 'center',
                    textWrap: 'nowrap',
                    color: '#ffffff',
                    fontSize: '14px',
                    lineHeight: '21px',
                    fontFamily: 'Noto Sans',
                    fontStyle: 'normal',
                    backgroundColor: '#2F54A5',
                    '&:hover': { backgroundColor: '#344e91' }
                  }}>
                  <Box
                    component="span"
                    sx={{
                      height: '10px',
                      width: '158px',
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={handleUnHiddenSmartCard}
                    >
                    Unhide All Smart Cards
                  </Box>
                </Button>
              </Box>
            </>
          <Box sx={{ display: 'flex', width: '100%', flexWrap: 'wrap', gap: '24px', justifyContent: 'left' }}>
            {showLoadingState ? (
              <SmartCardsLoading count={4} variant="grid" />
            ) : showNoHiddenSmartCards ? (
                 <NoAvailableReports isSmartCards />
            ) : (
              searchedSmarts.map((smart, idx) => (
                <Box
                  key={smart.scId || idx}
                  sx={{
                    flex: '0 1 calc(50% - 12px)',
                    maxWidth: 'calc(50% - 12px)',
                    minWidth: '300px',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                  <SmartCard
                    item={smart}
                    reportType={AppConfig.SMART_CAROUSEL}
                    showDismiss={showDismiss}
                    permissions={permissions}
                    setShowSmartCard={setShowSmartCard}
                    setSelectedSmartCardId={setSelectedSmartCardId}
                    hiddenCount={hiddenCount}
                    {...unhideTransitionHandlers}
                  />
                </Box>
              ))
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ width: '100%', maxWidth: { lg: 1328, xl: 1584 }, px: '24px', mx: 'auto', pt: 4 }}>
          {searchedSmarts && searchedSmarts.length > 0 && (
            <>
            <Link
              component={RouterLink}
              to="/bi/reports"
              onClick = { () => {
                sessionStorage.setItem('scrollToReports' , 'true')
              }}
              underline="none"
              sx={{
                fontSize: '14px',
                fontStyle: 'normal',
                fontFamily: 'Noto Sans',
                fontWeight: 600,
                color: '#2F54A5',
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: '150%',
                letterSpacing: '0%',
                gap: 0.5,
                '&:hover': { color: '#068DF1', textDecoration: 'none' },
                whiteSpace: 'nowrap',
                mb: 3,
              }}>
              <IconArrowNarrowLeft size={16} />
              Back
            </Link>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                <Box
                  sx={{
                    color: '#000',
                    fontFamily: 'Rubik',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '150%',
                    width: '922px',
                    height: '24px',
                    letterSpacing: '0.16px'
                  }}>
                  {`Hidden (${displaySmarts.length})`}
                </Box>
                <Button
                  disableElevation
                  sx={{
                    borderRadius: '24px',
                    height: '36px',
                    width: '182px',
                    textTransform: 'none',
                    padding: '8px 12px',
                    backgroundColor: '#2F54A5',
                    '&:hover': { backgroundColor: '#344e91' }
                  }}
                   onClick={handleUnHiddenSmartCard}
                  >
                  <Typography
                    component="span"
                    sx={{
                      height: '10px',
                      width: '158px',
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600, 
                      textWrap: 'nowrap',
                      color: '#ffffff',
                      fontSize: '14px',
                      lineHeight: '21px',
                      fontFamily: 'Noto Sans',
                      fontStyle: 'normal',
                    }}
                    >
                    Unhide All Smart Cards
                  </Typography>
                </Button>
              </Box>
            </>
          )}
          <Box sx={{ mt: 3 }}>
            {showLoadingState ? (
              <SmartCardsLoading count={6} variant="grid" />
            ) : showNoHiddenSmartCards ? (
              <NoAvailableReports isSmartCards />
            ) : (
              <Box sx={{
                display: 'inline-grid',
                width: '100%',
                rowGap: '24px',
                columnGap: '24px',
                alignSelf: 'stretch',
                gridTemplateRows: 'repeat(4, fit-content(100%))',
                gridTemplateColumns: { md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }
              }}>
                {searchedSmarts.map((smart, idx) => (
                  <Box
                    key={smart.scId || idx}
                    sx={{
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%'
                    }}>
                     <SmartCard
                      item={smart}
                      reportType={AppConfig.SMART_CAROUSEL}
                      showDismiss={showDismiss}
                      permissions={permissions}
                      setShowSmartCard={setShowSmartCard}
                      setSelectedSmartCardId={setSelectedSmartCardId}
                      hiddenCount={hiddenCount}
                      {...unhideTransitionHandlers}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Recovery Modal */}
      <Modal open={chartRecoveryModal} onClose={onRecoveryCancel}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '24px',
            border: '1px solid #959696',
            py: '40px',
            px: {
              xs: '16px',
              sm: '32px',
              md: '40px'
            },
            width: {
              xs: '95%',
              sm: '90%',
              md: '800px'
            },
            overflowY: 'visible',
            background: '#FFF'
          }}>
          {/* Title and Close button row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: '500', fontFamily: 'Rubik', fontSize: '20px', color: '#121A22', lineHeight: '30px' }}>
              Recover Smart Cards
            </Typography>
            <IconButton onClick={onRecoveryCancel} size="large" aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography sx={{ mb: 3, fontSize: '16px', color: '#121A22', fontFamily: 'Rubik', fontWeight: '400' }}>
            Select the smart cards you would like to recover and display again on your page.
          </Typography>
          <Box sx={{ maxHeight: '350px', overflowY: 'auto' }}>
            <FoMaterialTable
              columns={[
                { title: 'SMART CARD', field: 'name' },
                { title: 'ID', field: 'id', hidden: true }
              ]}
              data={recoverableCharts}
              options={{
                selection: true,
                search: false,
                paging: false,
                toolbar: false
              }}
              components={{ OverlayLoading: () => <div /> }}
              onSelectionChange={(rows) => {
                const ids = rows.map((row) => row.id);
                setSelectedRecoverableCharts(ids);
              }}
            />
          </Box>
          <Divider sx={{ width: '100%', mt: '40px', mb: '24px' }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '24px',
              flexDirection: {
                xs: 'column-reverse',
                sm: 'row'
              }
            }}>
            <Button
              variant="outlined"
              onClick={onRecoveryCancel}
              sx={{
                borderRadius: '24px',
                width: {
                  xs: '100%',
                  sm: '188px'
                },
                height: '48px',
                px: 3
              }}>
              Cancel
            </Button>
            <Button
              variant="outlined"
              onClick={recoverCharts}
              disabled={selectedRecoverableCharts.length === 0}
              sx={{
                borderRadius: '24px',
                width: {
                  xs: '100%',
                  sm: '188px'
                },
                height: '48px',
                px: 3,
                borderColor: '#2F54A5',
                color: '#2F54A5',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#2F54A5'
                }
              }}>
              Recover
            </Button>
          </Box>
        </Box>
      </Modal>
     {showSmartCard && (
        <HideSmartCardDialog
          open={showSmartCard}
          onClose={() => setShowSmartCard(false)}
          handleHideSmartCard={handleHideSmartCard}
          selectedSmartCardId={selectedSmartCardId}
        />
      )}
    </Box>
  );
};

export default HiddenSmartCard;